import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2, ScanText, Trash2 } from "lucide-react";
import { makeManualProforma, parseWaOrder, proformaParts, ron } from "@/lib/bf";

const FIELDS = [["salon", "Firmă / salon"], ["cui", "CUI"], ["admin_name", "Administrator"], ["phone", "Telefon"], ["email", "E-mail"], ["address", "Adresă livrare"]];

const ItemRow = ({ it, i, set, del }) => (
  <tr className={!it.code.trim() || !it.matched ? "is-bad" : ""} data-testid={`bf-rb-row-${i}`}>
    <td><input type="number" min="1" value={it.q} onChange={(e) => set({ ...it, q: Math.max(1, +e.target.value || 1) })} data-testid={`bf-rb-q-${i}`} /></td>
    <td><input value={it.code} placeholder="Cod lipsă" onChange={(e) => set({ ...it, code: e.target.value, matched: true })} data-testid={`bf-rb-code-${i}`} /></td>
    <td><input value={it.name} title={it.name} onChange={(e) => set({ ...it, name: e.target.value })} data-testid={`bf-rb-name-${i}`} /><small className={it.matched ? "is-hidden" : ""}>nu l-am găsit în catalog: verifică produsul și completează codul</small></td>
    <td><input type="number" step="0.01" min="0" value={it.unit} onChange={(e) => set({ ...it, unit: +e.target.value || 0 })} data-testid={`bf-rb-unit-${i}`} /></td>
    <td className="bf-rb-sum">{ron(it.unit * it.q)}</td>
    <td><button type="button" onClick={del} aria-label="Șterge" data-testid={`bf-rb-del-${i}`}><Trash2 size={14} /></button></td>
  </tr>
);

export default function BFRebuild({ onFree }) {
  const [text, setText] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const parse = () => {
    setBusy(true); setDone(null);
    parseWaOrder(text).then((r) => {
      if (!r.items.length && onFree) { toast.info("Nu e un mesaj generat de site. Îl citesc cu AI în „Proformă din mesajul clientei”."); onFree(text); setText(""); }
      else if (!r.items.length) toast.error("Nu am găsit produse în text.");
      setD(r.items.length ? r : null);
    })
      .catch(() => toast.error("Nu am putut citi mesajul")).finally(() => setBusy(false));
  };
  const bad = d ? d.items.filter((x) => !x.code.trim() || !x.matched).length : 0;
  const create = () => {
    if (bad && !window.confirm(`${bad} produse nu au cod sau nu au fost găsite în catalog. Creezi proforma oricum?`)) return;
    setBusy(true);
    const { items, lead_salon, ...rest } = d;
    makeManualProforma({ ...rest, items: items.map(({ q, name, unit, code }) => ({ q, name, unit, code })) })
      .then((r) => { setDone(r); toast.success(`Proforma ${r.no} a fost creată`); })
      .catch(() => toast.error("Nu am putut crea proforma")).finally(() => setBusy(false));
  };
  const setItem = (i) => (v) => setD({ ...d, items: d.items.map((x, j) => (j === i ? v : x)) });
  return (
    <section className="invite-box bf-rb" data-testid="bfa-rebuild">
      <h3>Reface proforma din mesajul WhatsApp</h3>
      <p className="st-hint">În WhatsApp apasă „Read more”, copiază tot mesajul comenzii și lipește-l aici. Prețul rămâne cel din mesaj, codul îl caut în catalog. Rândurile roșii trebuie verificate.</p>
      <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Bună ziua, ... Comandă Black Friday ..." data-testid="bf-rb-text" />
      <button type="button" className="st-btn st-btn--light" onClick={parse} disabled={busy || text.trim().length < 10} data-testid="bf-rb-parse">
        {busy && !d ? <Loader2 size={14} className="animate-spin" /> : <ScanText size={14} />} Citește mesajul
      </button>
      {d && (
        <>
          <div className="bf-rb-buyer">
            {FIELDS.map(([k, label]) => (
              <label key={k}>{label}<input value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} data-testid={`bf-rb-${k}`} /></label>
            ))}
          </div>
          <p className="st-hint" data-testid="bf-rb-lead">{d.lead ? `Legată de salonul înscris: ${d.lead_salon}` : "Salonul nu a fost găsit după CUI: proforma se creează doar cu datele de mai sus."}</p>
          <div className="bf-rb-table">
            <table>
              <thead><tr><th>Cant.</th><th>Cod</th><th>Produs</th><th>Preț/buc (TVA incl.)</th><th>Total</th><th /></tr></thead>
              <tbody>{d.items.map((it, i) => <ItemRow key={i} it={it} i={i} set={setItem(i)} del={() => setD({ ...d, items: d.items.filter((_, j) => j !== i) })} />)}</tbody>
            </table>
          </div>
          <p className="bf-rb-total" data-testid="bf-rb-total">{d.items.length} poziții · {d.items.reduce((s, x) => s + x.q, 0)} buc. · <b>{ron(d.items.reduce((s, x) => s + x.unit * x.q, 0))}</b>{bad > 0 && <em data-testid="bf-rb-bad"> · {bad} de verificat</em>}{d.items.length > 40 && ` · se împarte în ${Math.ceil(d.items.length / 40)} proforme`}</p>
          <button type="button" className="st-btn st-btn--silver" onClick={create} disabled={busy || !d.items.length} data-testid="bf-rb-create">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Creează proforma
          </button>
        </>
      )}
      {done && (
        <div className="share-actions" data-testid="bf-rb-done">
          {proformaParts(done).map((p) => (
            <span key={p.no} className="bfa-pf-part">
              <a className="st-btn st-btn--ghost" href={p.xlsx} data-testid={`bf-rb-xlsx-${p.no}`}><FileSpreadsheet size={14} /> Excel {p.no}</a>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
